import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Mail } from "lucide-react";
import { OTP_LENGTH, formatCountdown, type OtpCellsStatus } from "@nasam/ui";

/**
 * The OTP screen's marketing panel: the login art's copy, grid and stats
 * (LoginArtwork, "Login art" in styles.css) around a lock that mirrors the
 * form. The digits typed into the form's cells show up in the art's own
 * cells, the code's expiry ticks in its card, and a verified code opens the
 * lock with a "verified" pill over it.
 *
 * Laid out in the same 751x976 Figma units as the login art (--u), so the
 * two panels match. The scene isn't mirrored; the copy, cards and the cells'
 * reading order follow the language (first digit on the right in Arabic).
 */

/** n Figma units, at the panel's size. */
function u(n: number): string {
  return `calc(var(--u) * ${String(n)})`;
}

function at(
  x: number,
  y: number,
  width?: number,
  height?: number,
): CSSProperties {
  return {
    left: u(x),
    top: u(y),
    ...(width === undefined ? {} : { width: u(width) }),
    ...(height === undefined ? {} : { height: u(height) }),
  };
}

/** The dashed trail from the "code sent" card to the ring, in scene units. */
const TRAIL = "M248 488C282 494 296 528 300 566";

/** "3:30" as the card's "03:30". */
function clock(seconds: number): string {
  return formatCountdown(Math.max(0, seconds)).padStart(5, "0");
}

export interface OtpArtworkProps {
  /** The digits typed so far, as the form holds them. */
  readonly code: string;
  readonly status: OtpCellsStatus;
  /** The last check was rejected. */
  readonly error: boolean;
  /** Seconds until the current code expires. */
  readonly expiresIn: number;
  /** A code's full lifetime, for the validity stat. */
  readonly lifetime: number;
  readonly attemptsLeft: number;
}

export function OtpArtwork({
  code,
  status,
  error,
  expiresIn,
  lifetime,
  attemptsLeft,
}: OtpArtworkProps) {
  const { t, i18n } = useTranslation("authentication", { keyPrefix: "otpArt" });
  const dir = i18n.dir();
  const verified = status === "success";

  const stats = [
    { key: "encryption", value: "256-bit", tone: "lavender" },
    { key: "validity", value: formatCountdown(lifetime), tone: "amber" },
    { key: "attempts", value: String(attemptsLeft), tone: "green" },
  ] as const;

  return (
    <div
      className="login-art otp-art"
      data-status={status}
      data-error={error && !verified ? true : undefined}
    >
      <div className="login-art-scene" aria-hidden="true">
        <span
          className="login-art-glow otp-art-glow"
          style={at(95.5, 340, 560, 520)}
        />
        <span className="otp-art-ring" style={at(205.5, 430, 340, 340)} />
        <span
          className="otp-art-ring otp-art-ring-inner"
          style={at(260.5, 485, 230, 230)}
        />

        {/* From the "code sent" card to the ring, ending in a dot on it,
            with a spark running down it -- SMIL, so it follows the path in
            the viewBox's own units at any panel size. */}
        <svg viewBox="0 0 751 976" fill="none" className="otp-art-trail">
          <path d={TRAIL} />
          <circle cx="300" cy="568" r="3.5" />
          <circle r="2.6" className="otp-art-trail-spark">
            <animateMotion dur="2.4s" repeatCount="indefinite" path={TRAIL} />
          </circle>
        </svg>

        <div
          dir={dir}
          className="login-art-surface otp-art-sent"
          style={at(48, 454, 200, 62)}
        >
          <span className="otp-art-sent-icon">
            <Mail strokeWidth={2.4} />
          </span>
          <span className="otp-art-sent-text">
            <span className="otp-art-sent-title">{t("sentTitle")}</span>
            <span className="otp-art-sent-to">{t("sentTo")}</span>
          </span>
        </div>

        <span
          dir={dir}
          className="login-art-anchor otp-art-verified"
          style={at(375.5, 482)}
        >
          <span className="login-art-chip">
            <span className="login-art-dot" data-tone="lavender" />
            {t("verified")}
          </span>
        </span>

        <span className="otp-art-lock-halo" style={at(265.5, 490, 220, 220)} />
        <div className="otp-art-lock" style={at(305.5, 530, 140, 140)}>
          <svg viewBox="0 0 64 64" fill="none" className="otp-art-lock-icon">
            <path
              className="otp-art-shackle"
              d="M21 29V21C21 14.9 25.9 10 32 10C38.1 10 43 14.9 43 21V29"
              stroke="white"
              strokeWidth="6"
              strokeLinecap="round"
            />
            <rect x="14" y="27" width="36" height="28" rx="7" fill="white" />
            <circle cx="32" cy="39" r="3.6" fill="var(--art-purple)" />
            <rect
              x="30.4"
              y="40"
              width="3.2"
              height="7"
              rx="1.6"
              fill="var(--art-purple)"
            />
          </svg>
        </div>

        <div
          dir={dir}
          className="login-art-surface otp-art-expiry"
          style={at(514, 640, 188, 54)}
        >
          <span className="login-art-dot login-art-dot-live" data-tone="pink" />
          <span className="otp-art-expiry-label">{t("expiresIn")}</span>
          <span dir="ltr" className="otp-art-expiry-value">
            {clock(expiresIn)}
          </span>
        </div>

        <div dir={dir} className="otp-art-cells" style={at(191, 714, 369, 58)}>
          {Array.from({ length: OTP_LENGTH }, (_, index) => {
            const digit = code[index] ?? "";
            return (
              <span
                key={index}
                className="otp-art-cell"
                data-filled={digit === "" ? undefined : true}
                data-active={
                  !verified && index === code.length ? true : undefined
                }
                style={{ animationDelay: `${String(index * 70)}ms` }}
              >
                {/* Keyed by its digit so each new one pops in. */}
                {digit === "" ? null : (
                  <span key={digit} className="otp-art-digit">
                    {digit}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </div>

      <ul aria-hidden="true" dir={dir} className="login-art-stats">
        {stats.map(({ key, value, tone }) => (
          <li key={key} className="login-art-surface login-art-stat">
            <span className="login-art-dot" data-tone={tone} />
            <span dir="ltr" className="login-art-stat-value">
              {value}
            </span>
            <span className="login-art-stat-label">{t(`stats.${key}`)}</span>
          </li>
        ))}
      </ul>

      <div dir={dir} className="login-art-copy">
        <div aria-hidden="true" className="login-art-brand">
          <img src="/brand/logo.png" alt="" className="login-art-logo" />
          <span dir="ltr" className="login-art-wordmark">
            NASAM
          </span>
        </div>
        <p className="login-art-eyebrow">
          <span
            className="login-art-dot login-art-dot-live"
            data-tone="green"
          />
          {t("eyebrow")}
        </p>
        <h2 className="login-art-headline">
          {t("headline")} <br />
          <span className="login-art-headline-accent">
            {t("headlineAccent")}
          </span>
        </h2>
        <p className="login-art-lead">{t("lead")}</p>
      </div>
    </div>
  );
}
